// components/CircleSequence.jsx
"use client";

import React, { useMemo } from 'react';

const CircleSequence = ({
  sequence,               // Por ejemplo [0,1,2,...,249]
  totalPoints = 250,
  lineWidth = 2,
  positionToShow = 249,
}) => {
  const radius = 250;
  const margin = 50;
  const center = radius + margin;
  const size = (radius + margin) * 2;

  // Función que ubica un valor en el círculo (0..2π) - espejo horizontal + rotación 180°
  const getPointPosition = (pointValue) => {
    // Ajusta si tu secuencia maneja otro rango distinto [0..249].
    // Agregamos Math.PI para rotar 180 grados adicionales
    const angle = (pointValue / totalPoints) * 2 * Math.PI + Math.PI;
    // Mantenemos el espejo horizontal (- en lugar de +)
    const x = center - radius * Math.cos(angle);
    const y = center - radius * Math.sin(angle);
    return { x, y };
  };

  // Determinamos el índice máximo a mostrar
  const maxPosition = Math.min(positionToShow, sequence.length - 1);

  // Extraemos la subsecuencia que se "conectará" con líneas
  const sequenceToShow = useMemo(() => {
    if (maxPosition < 0) return [];
    return sequence.slice(0, maxPosition + 1);
  }, [sequence, maxPosition]);

  const lastShownIndex = sequenceToShow.length - 1;
  const secondToLastShownIndex = lastShownIndex - 1;

  // Líneas entre los puntos "mostrados"
  const lines = useMemo(() => {
    return sequenceToShow.slice(0, -1).map((pointValue, idx) => {
      const start = getPointPosition(pointValue);
      const end = getPointPosition(sequenceToShow[idx + 1]);
      return (
        <line
          key={idx}
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          stroke="black"
          strokeWidth={lineWidth}
        />
      );
    });
  }, [sequenceToShow, lineWidth]);

  // Dibujamos TODOS los puntos (azules por defecto, penúltimo y último en rojo)
  const allPoints = useMemo(() => {
    return sequence.map((pointValue, idx) => {
      const { x, y } = getPointPosition(pointValue);

      let fillColor = 'blue';
      let circleRadius = 2;

      if (idx === secondToLastShownIndex && secondToLastShownIndex >= 0) {
        fillColor = 'red';
        circleRadius = 4;
      }
      if (idx === lastShownIndex && lastShownIndex >= 0) {
        fillColor = 'red';
        circleRadius = 2;
      }

      return (
        <circle
          key={`point-${idx}`}
          cx={x}
          cy={y}
          r={circleRadius}
          fill={fillColor}
        />
      );
    });
  }, [sequence, lastShownIndex, secondToLastShownIndex]);

  // Marcadores numéricos cada 10 puntos - 0 a la derecha, aumenta antihorario
  const markers = useMemo(() => {
    const step = 10;
    const arr = [];
    for (let i = 0; i < totalPoints; i += step) {
      // Invertimos el ángulo para que aumente en sentido antihorario
      const angle = (i / totalPoints) * 2 * Math.PI;
      const startX = center + radius * Math.cos(angle);
      const startY = center + radius * Math.sin(angle);
      const endLineX = center + (radius + 10) * Math.cos(angle);
      const endLineY = center + (radius + 10) * Math.sin(angle);
      const textX = center + (radius + 25) * Math.cos(angle);
      const textY = center + (radius + 25) * Math.sin(angle);
      arr.push({
        startX,
        startY,
        endLineX,
        endLineY,
        textX,
        textY,
        label: i,
      });
    }
    return arr;
  }, [totalPoints, radius, center]);

  // Flecha roja gruesa apuntando al último punto mostrado
  let arrowLine = null;
  if (lastShownIndex >= 0) {
    const lastPointValue = sequenceToShow[lastShownIndex];
    const { x: lastX, y: lastY } = getPointPosition(lastPointValue);
    // Aplicamos espejo horizontal + rotación 180° también a la flecha
    const angleLastPoint = (lastPointValue / totalPoints) * 2 * Math.PI + Math.PI;
    const arrowStartRadius = radius + 40;
    const arrowStartX = center - arrowStartRadius * Math.cos(angleLastPoint);
    const arrowStartY = center - arrowStartRadius * Math.sin(angleLastPoint);

    arrowLine = (
      <line
        x1={arrowStartX}
        y1={arrowStartY}
        x2={lastX}
        y2={lastY}
        stroke="red"
        strokeWidth={4}
        markerEnd="url(#redArrow)"
      />
    );
  }

  return (
    <div className="w-full flex"> 
      <svg
        className="w-full h-auto block overflow-visible"
        viewBox={`0 0 ${size} ${size}`}
      >
        <defs>
          <marker
            id="redArrow"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="4"
            orient="auto"
          >
            <path d="M0,0 L0,8 L7,4 z" fill="red" />
          </marker>
        </defs>

        {/* Círculo de referencia */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="gray"
          strokeWidth="1"
        />

        {/* Líneas de la secuencia hasta positionToShow */}
        {lines}

        {/* Todos los puntos (azules, salvo el penúltimo y último en rojo) */}
        {allPoints}

        {/* Marcadores numéricos en pasos de 10 */}
        {markers.map(({ startX, startY, endLineX, endLineY, textX, textY, label }, idx) => (
          <g key={`marker-${idx}`}>
            <line
              x1={startX}
              y1={startY}
              x2={endLineX}
              y2={endLineY}
              stroke="black"
              strokeWidth={1}
            />
            <text
              x={textX}
              y={textY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="14"
              fontWeight="bold"
            >
              {label}
            </text>
          </g>
        ))}

        {/* Flecha roja gruesa apuntando al último punto mostrado */}
        {arrowLine}
      </svg>
    </div>
  );
};

export default CircleSequence;
